import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Button } from '@liminal/web-ui/primitives/Button';
import { Modal } from '@liminal/web-ui/overlays/Modal';
import { ConfirmationDialog } from '@liminal/web-ui/overlays/ConfirmationDialog';
import { Checkbox } from '@liminal/web-ui/forms/Checkbox';
import { Input } from '@liminal/web-ui/forms/Input';
import { Select } from '@liminal/web-ui/forms/Select';
import { Textarea } from '@liminal/web-ui/forms/Textarea';
import { FormErrorBanner } from '@liminal/web-ui/forms/FormErrorBanner';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import { useCreateAnnouncement, useUpdateAnnouncement } from '@/hooks/useAnnouncements';
import { pushAnnouncementStatusLabel, pushAnnouncementStatusVariant } from '@/components/ui/statusHelpers';
import type { AdminAnnouncement } from '@/api/types';
import { AnnouncementPreview } from './AnnouncementPreview';
import { PushAnnouncementPreview } from './PushAnnouncementPreview';
import { AudienceSelector, type AudienceSelection } from './AudienceSelector';
import { describeAudience } from './describeAudience';
import { useNow } from '@/hooks/useNow';
import { useOpeningKey } from '@/hooks/useOpeningKey';
import { getErrorMessage } from '@/errors/messages';
import { AnnouncementType, AudienceKind } from '@/api/generated';

const FormRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

// Form on the left, live preview on the right; they stack on a narrow window
// so the preview never squeezes the fields into unusability.
const Layout = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(3),
  alignItems: 'flex-start',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}));

const PreviewColumn = styled(Box)(({ theme }) => ({
  width: 240,
  flexShrink: 0,
  position: 'sticky',
  top: 0,
  [theme.breakpoints.down('md')]: {
    width: '100%',
    position: 'static',
  },
}));

const PUSH_TITLE_MAX = 255;
const PUSH_MESSAGE_MAX = 1000;
const PUSH_URL_MAX = 500;

// Proto enum values sent to gRPC-gateway
const ANNOUNCEMENT_TYPES = [
  { value: AnnouncementType.ANNOUNCEMENT_TYPE_MAINTENANCE, label: 'Maintenance' },
  { value: AnnouncementType.ANNOUNCEMENT_TYPE_FEATURE, label: 'Feature' },
  { value: AnnouncementType.ANNOUNCEMENT_TYPE_URGENT, label: 'Urgent' },
  { value: AnnouncementType.ANNOUNCEMENT_TYPE_PROMO, label: 'Promo' },
];

function toLocalDatetime(isoString?: string): string {
  if (!isoString) return '';
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return '';
  // Format: YYYY-MM-DDThh:mm
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Returns the first problem with the push fields, or an empty string when they
 * are fine. Each override falls back to the announcement's own copy, so the
 * length checks run against whatever will actually be sent.
 */
function validatePush(fields: {
  pushTitle: string;
  pushMessage: string;
  pushUrl: string;
  title: string;
  body: string;
  actionUrl: string;
}): string {
  const effectiveTitle = fields.pushTitle.trim() || fields.title.trim();
  const effectiveMessage = fields.pushMessage.trim() || fields.body.trim();
  const effectiveUrl = fields.pushUrl.trim() || fields.actionUrl.trim();

  if (effectiveTitle.length > PUSH_TITLE_MAX) {
    return `Push title must be ${PUSH_TITLE_MAX} characters or fewer`;
  }
  if (effectiveMessage.length > PUSH_MESSAGE_MAX) {
    return `Push message must be ${PUSH_MESSAGE_MAX} characters or fewer`;
  }
  if (effectiveUrl) {
    if (effectiveUrl.length > PUSH_URL_MAX) {
      return `Push deep link must be ${PUSH_URL_MAX} characters or fewer`;
    }
    // Mirrors the backend / mobile internal-app-path gate.
    if (
      !effectiveUrl.startsWith('/') ||
      effectiveUrl.startsWith('//') ||
      effectiveUrl.includes('://')
    ) {
      return 'Push deep link must be an internal app path starting with / (e.g. /savings)';
    }
  }
  return '';
}

interface AnnouncementFormDialogProps {
  open: boolean;
  onClose: () => void;
  announcement?: AdminAnnouncement; // If provided, edit mode
  /**
   * Seed a NEW announcement from an existing one. Copies the copy — title,
   * body, type, action, priority — and nothing else: the schedule is reset,
   * because a start date lifted from a past announcement would create one that
   * is already over. Ignored when `announcement` is set (that is an edit).
   */
  copyFrom?: AdminAnnouncement;
}

export function AnnouncementFormDialog(props: AnnouncementFormDialogProps) {
  // The form's state belongs to one opening of the dialog, for one source.
  const openingKey = useOpeningKey(props.open, props.announcement ?? props.copyFrom);
  return <AnnouncementForm key={openingKey} {...props} />;
}

function AnnouncementForm({ open, onClose, announcement, copyFrom }: AnnouncementFormDialogProps) {
  const isEdit = !!announcement;
  const isCopy = !isEdit && !!copyFrom;
  const createMutation = useCreateAnnouncement();
  const updateMutation = useUpdateAnnouncement();

  // An edit or a copy starts from its source; a copy keeps the copy and drops
  // the schedule (see `copyFrom`), so only an edit starts from stored dates.
  const source = announcement ?? copyFrom;
  const [title, setTitle] = useState(source?.title ?? '');
  const [body, setBody] = useState(source?.body ?? '');
  const [type, setType] = useState<AnnouncementType>(source?.type || AnnouncementType.ANNOUNCEMENT_TYPE_MAINTENANCE);
  const [actionLabel, setActionLabel] = useState(source?.actionLabel ?? '');
  const [actionUrl, setActionUrl] = useState(source?.actionUrl ?? '');
  const [priority, setPriority] = useState(source?.priority ?? 0);
  const [startsAt, setStartsAt] = useState(() =>
    toLocalDatetime(announcement ? announcement.startsAt : new Date().toISOString()),
  );
  const [expiresAt, setExpiresAt] = useState(() => (announcement ? toLocalDatetime(announcement.expiresAt) : ''));
  const [error, setError] = useState('');
  const [audience, setAudience] = useState<AudienceSelection>({ valid: true, summary: 'all users' });
  // Edit mode keeps the stored targeting untouched unless the admin opts in —
  // an omitted audience means "keep existing" all the way to the DB row.
  const [changeAudience, setChangeAudience] = useState(false);
  // Broadening a targeted announcement to everyone is confirmed explicitly:
  // an admin who clicked "Change" just to look must not silently retarget.
  const [confirmRetargetAll, setConfirmRetargetAll] = useState(false);
  // A push is a one-shot send, so it is offered on create and copy only — an
  // edit shows the status of whatever was already sent instead.
  const [sendPush, setSendPush] = useState(false);
  const [pushTitle, setPushTitle] = useState('');
  const [pushMessage, setPushMessage] = useState('');
  const [pushUrl, setPushUrl] = useState('');
  const [confirmPush, setConfirmPush] = useState(false);

  const isPending = createMutation.isPending || updateMutation.isPending;
  const pushEnabled = !isEdit && sendPush;

  // The preview and the payload agree on the fallbacks: what an empty override
  // field shows is exactly what the server will send.
  const effectivePushTitle = pushTitle.trim() || title;
  const effectivePushMessage = pushMessage.trim() || body;
  const effectivePushUrl = pushUrl.trim() || actionUrl.trim();
  const pushSendAt = new Date(startsAt);
  const now = useNow(30_000);
  const pushScheduledFor = !isNaN(pushSendAt.getTime()) && pushSendAt.getTime() > now ? pushSendAt : null;

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    if (!body.trim()) {
      setError('Body is required');
      return;
    }
    if (!startsAt) {
      setError('Start date is required');
      return;
    }
    const audienceActive = !isEdit || changeAudience;
    if (audienceActive && !audience.valid) {
      setError(audience.error ?? 'Select an audience');
      return;
    }
    if (pushEnabled) {
      const pushError = validatePush({ pushTitle, pushMessage, pushUrl, title, body, actionUrl });
      if (pushError) {
        setError(pushError);
        return;
      }
    }
    setError('');

    // Saving a previously-targeted announcement with an all-users selection
    // broadens it to everyone — confirm before doing that.
    const broadensToAll =
      isEdit &&
      changeAudience &&
      announcement.audienceKind === AudienceKind.AUDIENCE_KIND_USER_IDS &&
      audience.audience === undefined;
    if (broadensToAll && !confirmRetargetAll) {
      setConfirmRetargetAll(true);
      return;
    }

    // A push reaches real devices and cannot be recalled once the fan-out has
    // started, so it is confirmed separately from the announcement itself.
    if (pushEnabled && !confirmPush) {
      setConfirmPush(true);
      return;
    }

    const payload = {
      title: title.trim(),
      body: body.trim(),
      type,
      priority,
      startsAt: new Date(startsAt).toISOString(),
      ...(actionLabel.trim() && { actionLabel: actionLabel.trim() }),
      ...(actionUrl.trim() && { actionUrl: actionUrl.trim() }),
      ...(expiresAt && { expiresAt: new Date(expiresAt).toISOString() }),
    };

    try {
      if (isEdit) {
        await updateMutation.mutateAsync({
          id: announcement.id,
          updatedAt: announcement.updatedAt,
          ...payload,
          // Omitted audience keeps the stored targeting; an explicit ALL is
          // needed to actively retarget back to everyone.
          ...(changeAudience && {
            audience: audience.audience ?? { kind: AudienceKind.AUDIENCE_KIND_ALL },
          }),
        });
      } else {
        await createMutation.mutateAsync({
          ...payload,
          ...(audience.audience && { audience: audience.audience }),
          // An empty override means "use the announcement's own copy", which
          // the server does for any field it is not sent.
          ...(pushEnabled && {
            push: {
              ...(pushTitle.trim() && { title: pushTitle.trim() }),
              ...(pushMessage.trim() && { message: pushMessage.trim() }),
              ...(pushUrl.trim() && { url: pushUrl.trim() }),
            },
          }),
        });
      }
      onClose();
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setConfirmRetargetAll(false);
      setConfirmPush(false);
    }
  };

  return (
    <Modal
      open={open}
      title={isEdit ? 'Edit Announcement' : isCopy ? 'Copy Announcement' : 'Create Announcement'}
      onClose={onClose}
      width="lg"
      actions={
        <>
          <Button variant="link" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} loading={isPending}>
            {isEdit ? 'Save Changes' : 'Create'}
          </Button>
        </>
      }
    >
      <Layout sx={{ pt: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Input label="Title" value={title} onChange={setTitle} required size="sm" />
          <Textarea label="Body" value={body} onChange={setBody} required rows={3} />
          <FormRow>
            <Box sx={{ minWidth: 180, flex: 1 }}>
              <Select
                label="Type"
                size="sm"
                value={type}
                onChange={(value) => {
                  const next = ANNOUNCEMENT_TYPES.find((option) => option.value === value);
                  if (next) setType(next.value);
                }}
                options={ANNOUNCEMENT_TYPES}
              />
            </Box>
            <Box sx={{ width: 140 }}>
              <Input
                label="Priority"
                type="number"
                size="sm"
                value={String(priority)}
                onChange={(value) => setPriority(Number(value))}
              />
            </Box>
          </FormRow>
          <FormRow>
            <Box sx={{ flex: 1 }}>
              <Input label="Action Label" size="sm" value={actionLabel} onChange={setActionLabel} placeholder="e.g. Learn More" />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Input label="Action URL" size="sm" value={actionUrl} onChange={setActionUrl} placeholder="e.g. /savings" />
            </Box>
          </FormRow>
          <FormRow>
            <Box sx={{ flex: 1 }}>
              <Input label="Starts At" type="datetime-local" size="sm" value={startsAt} onChange={setStartsAt} required />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Input label="Expires At" type="datetime-local" size="sm" value={expiresAt} onChange={setExpiresAt} />
            </Box>
          </FormRow>
          {isEdit && !changeAudience ? (
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Audience: {describeAudience(announcement.audienceKind, announcement.audienceDescription, announcement.audienceSize)}
              </Typography>
              <Button variant="link" onClick={() => setChangeAudience(true)}>
                Change
              </Button>
            </Box>
          ) : (
            <AudienceSelector key={String(open)} onChange={setAudience} disabled={isPending} />
          )}

          {isEdit ? (
            announcement.pushAnnouncementId ? (
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Push notification:
                </Typography>
                <StatusPill
                  label={pushAnnouncementStatusLabel(announcement.pushStatus)}
                  tone={pushAnnouncementStatusVariant(announcement.pushStatus)}
                  size="sm"
                />
              </Box>
            ) : null
          ) : (
            <Box sx={{ mb: 2 }}>
              <Checkbox
                label="Also send a push notification"
                helper="Sends to the same audience when the announcement starts. Leave a field blank to reuse the announcement's own copy."
                checked={sendPush}
                onChange={setSendPush}
                disabled={isPending}
                data-testid="announcement-send-push"
              />
              {sendPush && (
                <Box sx={{ mt: 2 }}>
                  <Input
                    label="Push Title"
                    size="sm"
                    value={pushTitle}
                    onChange={setPushTitle}
                    placeholder={title || 'Announcement title'}
                  />
                  <Textarea
                    label="Push Message"
                    value={pushMessage}
                    onChange={setPushMessage}
                    rows={2}
                    placeholder={body || 'Announcement body'}
                  />
                  <Input
                    label="Push Deep Link"
                    size="sm"
                    value={pushUrl}
                    onChange={setPushUrl}
                    placeholder={actionUrl.trim() || 'e.g. /savings'}
                  />
                </Box>
              )}
            </Box>
          )}

          <FormErrorBanner error={error || undefined} />
        </Box>

        <PreviewColumn>
          <AnnouncementPreview
            title={title}
            body={body}
            type={type}
            actionLabel={actionLabel}
            actionUrl={actionUrl}
          />
          {pushEnabled && (
            <Box sx={{ mt: 2 }}>
              <PushAnnouncementPreview
                title={effectivePushTitle}
                message={effectivePushMessage}
                url={effectivePushUrl}
                scheduledFor={pushScheduledFor}
              />
            </Box>
          )}
        </PreviewColumn>
      </Layout>

      <ConfirmationDialog
        open={confirmRetargetAll}
        title="Retarget to all users?"
        message={`This announcement currently targets ${describeAudience(announcement?.audienceKind, announcement?.audienceDescription, announcement?.audienceSize).toLowerCase()}. Saving will show it to every user instead.`}
        confirmLabel="Retarget to everyone"
        destructive
        loading={isPending}
        onConfirm={() => void handleSubmit()}
        onCancel={() => setConfirmRetargetAll(false)}
        data-testid="confirm-retarget-all"
      />

      <ConfirmationDialog
        open={confirmPush}
        title={pushScheduledFor ? 'Schedule this push?' : 'Send this push now?'}
        message={`A push notification will go to ${audience.summary}${
          pushScheduledFor ? ` when the announcement starts, at ${pushScheduledFor.toLocaleString()}` : ' immediately'
        }. It cannot be recalled once sending has started.`}
        confirmLabel={pushScheduledFor ? 'Schedule push' : 'Send push now'}
        loading={isPending}
        onConfirm={() => void handleSubmit()}
        onCancel={() => setConfirmPush(false)}
        data-testid="confirm-announcement-push"
      />
    </Modal>
  );
}
